import React from "react";
import {
  Spin,
  Image,
  Upload,
  message,
  Form,
  Input,
  Button,
  Select,
  Modal,
  Checkbox,
} from "antd";
import { LoadingOutlined, PlusOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../common/utils/notification";
import moment from "moment";
import {
  _get_store_profile,
  _create_store_business,
  _update_store_profile,
} from "../../common/axios_services";
import { useSelector } from "react-redux";
import AllAppRoutes from "../../common/routes";
import { suggestedStoreBanners } from "../../common/constant";
import StateLgaSelects from "../../components/common/StateLgaSelects";
import useUserProfile from "../../hooks/useUserProfile";
import AutoCompleteAddress from "../../components/AutoCompleteAddress";

const StoreBusiness = ({ user }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const newAntIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#008060" }} spin />
  );

  const { userProfile: fetchedUserProfile, refetchUserProfile } =
    useUserProfile();

  const [spinnerLoading, setSpinnerLoading] = React.useState(false);
  const [banner_url, setBannerUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [updatingProfile, setUpdatingProfile] = React.useState(false);
  const [isBannerModalOpen, setIsBannerModalOpen] = React.useState(false);
  const [showDifferentSupplyAddress, setShowDifferentSupplyAddress] =
    React.useState(false);
  const [contactModalVisible, setContactModalVisible] = React.useState(false);
  const [contactPerson, setContactPerson] = React.useState("");
  const [contactTelephone, setContactTelephone] = React.useState("");
  const [contactForm] = Form.useForm();

  const showSuggestions = () => {
    setIsBannerModalOpen(true);
  };

  const selectBanner = (url) => {
    setBannerUrl(url);
    setIsBannerModalOpen(false);
  };

  const handleModalClose = () => {
    setIsBannerModalOpen(false);
  };

  const getBase64 = (img, callback) => {
    const reader = new FileReader();
    reader.readAsDataURL(img);
    reader.onload = () => callback(reader.result);
    reader.onerror = (error) => console.error("Error reading file:", error);
  };

  const preBannerUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return Upload.LIST_IGNORE;
    }

    const isLt5MB = file.size / 1024 / 1024 < 5;
    if (!isLt5MB) {
      message.error("Image must be smaller than 5MB!");
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const uploadButton = (
    <div
      style={{
        width: "100%",
        height: "300px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload Store Banner</div>
    </div>
  );

  const finishProfile = async (values) => {
    const data = {
      name: values.name,
      bannerImage: banner_url || "",
      supplyAddress: values.showDifferentSupplyAddress
        ? values.supplyAddressString
        : undefined,
      contactPerson: contactPerson,
      contactTelephone: contactTelephone,
      // openingTime: values.open_time,
    };

    try {
      setUpdatingProfile(true);
      let response;

      // Try to update first, if it fails with 404, create new
      try {
        response = await _update_store_profile(data);
      } catch (updateError) {
        if (updateError.response?.status === 404) {
          // Business doesn't exist, create it
          response = await _create_store_business(data);
        } else {
          throw updateError;
        }
      }

      showSuccessNotification(response?.data?.message);
      refetchUserProfile();
      // navigate(AllAppRoutes.restaurant_dashboard);
    } catch (err) {
      showErrorNotification(err.response?.data?.message ?? err.message);
    } finally {
      setUpdatingProfile(false);
    }
  };

  const fetchStoreProfile = async () => {
    try {
      setSpinnerLoading(true);
      const response = await _get_store_profile();
      const data = response.data.data || {};

      setBannerUrl(data.bannerImage);
      setContactPerson(data.contactPerson || user?.userName || "");
      setContactTelephone(data.contactTelephone || user?.phoneNumber || "");

      if (data.supplyAddress) {
        setShowDifferentSupplyAddress(true);
      } else {
        setShowDifferentSupplyAddress(false);
      }

      form.setFieldsValue({
        name: data.name || user?.business?.name,
        supplyAddressString: data.supplyAddress,
        supplyState: data.supplyState,
        supplyLga: data.supplyLga,
        // open_time: data.openingTime,
        showDifferentSupplyAddress: data.supplyAddress ? true : false,
      });
    } catch (err) {
      if (err.response?.status === 404) {
        // Store business not set up yet, use defaults
        setContactPerson(user?.userName || "");
        setContactTelephone(user?.phoneNumber || "");
        form.setFieldsValue({
          name: user?.business?.name,
          open_time: null,
          showDifferentSupplyAddress: false,
        });
      } else {
        showErrorNotification(err.response?.data?.message ?? err.message);
      }
    } finally {
      setSpinnerLoading(false);
    }
  };

  React.useEffect(() => {
    if (fetchedUserProfile) {
      fetchStoreProfile();
    }
  }, [fetchedUserProfile]);

  const generateTimeOptions = () => {
    const times = [];
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 60; j += 15) {
        const time = moment({ hour: i, minute: j });
        times.push(time.format("h:mm A"));
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();
  const openTimeOptions = timeOptions;

  const { Option } = Select;

  const handleContactModalOpen = () => {
    contactForm.setFieldsValue({
      contact_person: contactPerson,
      contact_telephone: contactTelephone,
    });
    setContactModalVisible(true);
  };

  const handleContactModalClose = () => {
    setContactModalVisible(false);
    contactForm.resetFields();
  };

  const handleContactSave = async (values) => {
    try {
      setUpdatingProfile(true);
      const updateData = {
        contactPerson: values.contact_person,
        contactTelephone: values.contact_telephone,
      };

      const updateStoreProfile = await _update_store_profile(updateData);
      showSuccessNotification(updateStoreProfile?.data?.message);

      setContactPerson(values.contact_person);
      setContactTelephone(values.contact_telephone);
      handleContactModalClose();
      refetchUserProfile();
    } catch (err) {
      showErrorNotification(err.response?.data?.message ?? err.message);
    } finally {
      setUpdatingProfile(false);
    }
  };

  return (
    <Spin spinning={spinnerLoading} indicator={newAntIcon}>
      <div
        style={{ marginBottom: "20px" }}
        className="avatar-uploader"
        onClick={showSuggestions}
      >
        <Upload
          name="banner"
          showUploadList={false}
          multiple={false}
          beforeUpload={() => false}
          style={{ pointerEvents: "none", width: "100%" }}
        >
          {banner_url ? (
            <img
              src={banner_url}
              alt="store-banner"
              style={{
                width: "100%",
                height: "300px",
                objectFit: "cover",
              }}
            />
          ) : (
            uploadButton
          )}
        </Upload>
      </div>

      <Modal
        centered
        open={isBannerModalOpen}
        title="Choose a Store Banner"
        onCancel={handleModalClose}
        footer={null}
        width="80vw"
        bodyStyle={{
          maxHeight: "70vh",
          overflowY: "auto",
          padding: "16px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: "15px",
            padding: "10px",
          }}
        >
          {suggestedStoreBanners.map((banner) => (
            <div
              key={banner}
              style={{
                position: "relative",
                cursor: "pointer",
                border:
                  banner === banner_url
                    ? "2px solid #1890ff"
                    : "1px solid #ddd",
                borderRadius: "4px",
                overflow: "hidden",
                aspectRatio: "16/9",
              }}
              onClick={() => {
                selectBanner(banner);
                handleModalClose();
              }}
            >
              <img
                src={banner}
                alt="suggested-banner"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={handleModalClose}>Close</Button>
        </div>
      </Modal>

      <Form
        form={form}
        layout="vertical"
        onFinish={finishProfile}
        initialValues={{ name: user?.business?.name }}
        style={{ padding: "20px" }}
      >
        <p className="profile_category_title">Contact Information</p>
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Contact Person</p>
                  <p className="font-medium text-gray-900">
                    {contactPerson || "Not set"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    Contact Telephone
                  </p>
                  <p className="font-medium text-gray-900">
                    {contactTelephone || "Not set"}
                  </p>
                </div>
              </div>
            </div>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={handleContactModalOpen}
              className="text-gray-600 hover:text-gray-900"
            >
              Change
            </Button>
          </div>
        </div>

        <Form.Item
          label="Store name"
          name="name"
          rules={[
            {
              required: true,
              message: "Store name is required",
            },
          ]}
        >
          <Input placeholder="Enter store name" />
        </Form.Item>

        <p className="profile_category_title">Supply Information</p>

        <Form.Item name="showDifferentSupplyAddress" valuePropName="checked">
          <Checkbox
            checked={showDifferentSupplyAddress}
            onChange={(e) => {
              setShowDifferentSupplyAddress(e.target.checked);
            }}
          >
            Supply address, different from business address
          </Checkbox>
        </Form.Item>

        {showDifferentSupplyAddress && (
          <>
            <StateLgaSelects
              form={form}
              initialPickupState={form.getFieldValue("supplyState")}
              initialPickupLga={form.getFieldValue("supplyLga")}
              stateFieldName="supplyState"
              lgaFieldName="supplyLga"
            />

            <Form.Item
              label="Supply Address"
              name="supplyAddressString"
              rules={[
                {
                  required: true,
                  message: "Please enter the supply address",
                },
              ]}
            >
              <AutoCompleteAddress
                placeholder="Supply address"
                style={{ height: "5rem" }}
              />
            </Form.Item>
          </>
        )}

        {/* <p className="profile_category_title">Business Hours</p>

        <div>
          <Form.Item
            label="Opening time"
            name="open_time"
            rules={[
              {
                required: true,
                message: "Please select your opening time!",
              },
            ]}
          >
            <Select
              placeholder="Select opening time"
              size="large"
              style={{ width: "100%" }}
            >
              {openTimeOptions.map((time) => (
                <Option key={time} value={time}>
                  {time}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div> */}

        <div>
          <Form.Item label="Description" name="description">
            <Input.TextArea
              placeholder="Enter description here..."
              rows={6}
              className="custom-textarea"
            />
          </Form.Item>
        </div>

        {updatingProfile || !banner_url ? (
          <button
            className="restaurant_profile_button bg-gray-300 text-gray-500 cursor-not-allowed border-none px-6 py-3 rounded-lg flex items-center justify-center transition-colors"
            disabled
          >
            {updatingProfile ? (
              <>
                Updating profile. Please wait
                <Spin style={{ marginLeft: "10px" }} indicator={newAntIcon} />
              </>
            ) : (
              <>Please upload a store banner to proceed</>
            )}
          </button>
        ) : (
          <button
            className="restaurant_profile_button bg-blue-500 text-white hover:bg-blue-600 border-none px-6 py-3 rounded-lg transition-colors"
            htmlType="submit"
          >
            Update Profile
          </button>
        )}
      </Form>

      <Modal
        title="Update Contact Information"
        open={contactModalVisible}
        onCancel={handleContactModalClose}
        footer={null}
        centered
      >
        <Form form={contactForm} layout="vertical" onFinish={handleContactSave}>
          <Form.Item
            label="Contact Person"
            name="contact_person"
            rules={[
              {
                required: true,
                message: "Please enter contact person name",
              },
            ]}
          >
            <Input placeholder="Enter contact person name" />
          </Form.Item>

          <Form.Item
            label="Contact Telephone"
            name="contact_telephone"
            rules={[
              {
                required: true,
                message: "Please enter contact telephone",
              },
            ]}
          >
            <Input placeholder="Enter contact telephone" />
          </Form.Item>

          <div className="flex justify-end space-x-2 mt-4">
            <Button onClick={handleContactModalClose}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updatingProfile}
              className="bg-gray-900"
            >
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal>
    </Spin>
  );
};

export default StoreBusiness;
